import { Container } from '@mui/material';
import styled from 'styled-components';
import { color, duration, ease, font, radius, shadow } from '../../../theme/tokens';
import { floaty, reducedMotion } from '../../atome/motion/motion.styled';

export const HeroWrapper = styled.section`
  position: relative;
  overflow: hidden;
  padding: clamp(2.5rem, 6vw, 5rem) 0 clamp(3rem, 7vw, 5.5rem);
  background:
    radial-gradient(80% 70% at 78% 18%, rgba(255, 255, 255, 0.72) 0%, transparent 60%),
    linear-gradient(135deg, ${color.sand} 0%, ${color.sandLight} 42%, ${color.cream} 100%);
`;

/** Faint concentric rings — texture without an image request. */
export const HeroRings = styled.div`
  position: absolute;
  top: -18%;
  right: -12%;
  width: 46rem;
  height: 46rem;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.55);
  box-shadow:
    inset 0 0 0 60px rgba(255, 255, 255, 0.22),
    inset 0 0 0 160px rgba(255, 255, 255, 0.14);
  pointer-events: none;
`;

export const CustomContainer = styled(Container)`
  position: relative;
  z-index: 1;
`;

export const HeroCopy = styled.div`
  max-width: 34rem;

  h1 {
    font-family: ${font.display};
    font-size: clamp(2.7rem, 6.2vw, 4.6rem);
    line-height: 1.02;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: ${color.ink};
    margin: 1.1rem 0 1rem;
  }

  h1 em {
    font-style: italic;
    color: ${color.forest};
  }

  p {
    font-family: ${font.body};
    font-size: 1.02rem;
    line-height: 1.8;
    color: ${color.inkSoft};
    margin: 0 0 2rem;
    max-width: 40ch;
  }
`;

export const HeroActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.9rem;
  flex-wrap: wrap;
`;

export const PrimaryCta = styled.button`
  position: relative;
  overflow: hidden;
  padding: 0.95rem 2rem;
  border: none;
  border-radius: ${radius.pill};
  cursor: pointer;
  font-family: ${font.body};
  font-size: 0.95rem;
  font-weight: 500;
  letter-spacing: 0.05em;
  color: #fff;
  background: ${color.forest};
  box-shadow: 0 16px 34px rgba(74, 112, 97, 0.28);
  transition:
    transform ${duration.base} ${ease.spring},
    box-shadow ${duration.base} ${ease.out},
    background ${duration.base} ${ease.out};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -120%;
    width: 55%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
    transition: left 0.75s ${ease.out};
  }

  &:hover {
    background: ${color.forestDeep};
    transform: translateY(-3px);
    box-shadow: 0 22px 44px rgba(74, 112, 97, 0.34);
  }

  &:hover::before {
    left: 130%;
  }

  ${reducedMotion};
`;

export const GhostCta = styled.button`
  padding: 0.9rem 1.6rem;
  border-radius: ${radius.pill};
  cursor: pointer;
  font-family: ${font.body};
  font-size: 0.95rem;
  letter-spacing: 0.05em;
  color: ${color.forestDeep};
  background: rgba(255, 255, 255, 0.5);
  border: 1px solid rgba(74, 112, 97, 0.28);
  transition:
    background ${duration.base} ${ease.out},
    transform ${duration.base} ${ease.spring},
    border-color ${duration.base} ${ease.out};

  &:hover {
    background: #fff;
    transform: translateY(-3px);
    border-color: ${color.forest};
  }

  ${reducedMotion};
`;

export const HeroMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1.4rem;
  margin-top: 2.4rem;
  flex-wrap: wrap;

  span {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    font-family: ${font.body};
    font-size: 0.82rem;
    color: ${color.inkSoft};
  }

  svg {
    color: ${color.forest};
    font-size: 1.05rem;
  }
`;

/* ------------------------- 3D carousel ------------------------- */

export const CarouselShell = styled.div`
  position: relative;
  display: flex;
  justify-content: center;

  /* soft halo behind the stack */
  &::before {
    content: '';
    position: absolute;
    inset: 12% 8%;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.7), transparent 68%);
    filter: blur(14px);
  }
`;

export const CarouselWrapper = styled.div`
  position: relative;
  width: min(100%, 22rem);
  height: 26rem;
  perspective: 900px;
  transform-style: preserve-3d;
  border-radius: ${radius.lg};

  @media (min-width: 768px) {
    width: min(100%, 26rem);
    height: 31rem;
  }

  @media (min-width: 1200px) {
    width: min(100%, 29rem);
    height: 35rem;
  }
`;

export const CardContainer = styled.div<{ $style: React.CSSProperties; $zIndex: number }>`
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: ${({ $zIndex }) => $zIndex};
  transform: ${({ $style }) => $style.transform};
  filter: ${({ $style }) => $style.filter};
  opacity: ${({ $style }) => $style.opacity};
  pointer-events: ${({ $style }) => $style.pointerEvents};
  display: ${({ $style }) => $style.display};
  transition:
    transform 1.4s ${ease.out},
    filter 1.4s ${ease.out},
    opacity 1.4s ${ease.out};
  ${reducedMotion};
`;

export const ImageCard = styled.div`
  width: 100%;
  height: 100%;
  border-radius: ${radius.lg};
  overflow: hidden;
  background: linear-gradient(150deg, ${color.sandLight}, ${color.cream});
  box-shadow: ${shadow.lift};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

/** Small caption chip that names the active slide. */
export const SlideCaption = styled.div`
  position: absolute;
  left: 50%;
  bottom: -1.1rem;
  transform: translateX(-50%);
  z-index: 1200;
  padding: 0.55rem 1.15rem;
  border-radius: ${radius.pill};
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(8px);
  box-shadow: ${shadow.soft};
  font-family: ${font.body};
  font-size: 0.78rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${color.forestDeep};
  white-space: nowrap;
  animation: ${floaty} 6s ${ease.inOut} infinite;
  ${reducedMotion};
`;

export const NavButton = styled.button<{ direction: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  ${({ direction }) => (direction === 'left' ? 'left: -0.6rem;' : 'right: -0.6rem;')}
  transform: translateY(-50%);
  z-index: 1300;
  width: 44px;
  height: 44px;
  display: grid;
  place-items: center;
  cursor: pointer;
  user-select: none;
  border: none;
  border-radius: 50%;
  font-size: 1.1rem;
  color: ${color.forestDeep};
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(8px);
  box-shadow: ${shadow.soft};
  transition:
    background ${duration.fast} ${ease.out},
    transform ${duration.fast} ${ease.spring};

  &:hover {
    background: #fff;
    transform: translateY(-50%) scale(1.1);
  }

  ${reducedMotion};
`;

export const Dots = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 2.6rem;
`;

export const Dot = styled.button<{ $active: boolean }>`
  width: ${({ $active }) => ($active ? '26px' : '8px')};
  height: 8px;
  padding: 0;
  border: none;
  border-radius: ${radius.pill};
  cursor: pointer;
  background: ${({ $active }) => ($active ? color.forest : 'rgba(74, 112, 97, 0.28)')};
  transition:
    width ${duration.base} ${ease.spring},
    background ${duration.base} ${ease.out};
`;
